import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Button } from 'primeng/button';
import { catchError, defer, EMPTY, expand, finalize, last, of, switchMap, takeWhile, tap, timer } from 'rxjs';
import { OrgConfigs } from '../shared/models/org-configs';
import { DataService } from '../shared/service/dataService';
import { NotificationService } from '../shared/service/notification.service';
import CommonUtils from '../shared/utils/common.utils';
import { AdditionalInfoComponent } from './additional-info/additional-info.component';
import { ExhibitorInfoComponent } from './exhibitor-info/exhibitor-info.component';
import { LivestocksComponent } from './livestocks/livestocks.component';
import { County } from './models/county';
import { Livestock, Registration, RegistrationResponse } from './models/registration';
import { State } from './models/state';
import { Steps } from './models/steps.enum';
import { PaymentComponent } from './payment/payment.component';
import { RegistrationService } from './registration.service';

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrl: './registration.component.scss'
})
export class RegistrationComponent implements OnInit {

  @ViewChild('exhibitorInfo') exhibitorInfo!: ExhibitorInfoComponent;
  @ViewChild('livestocksInfo') livestocksInfo!: LivestocksComponent;
  @ViewChild('additionalInfo') additionalInfo!: AdditionalInfoComponent;
  @ViewChild('paymentInfo') paymentInfo!: PaymentComponent;

  minQty: number = 0;
  stepperOrientation: 'vertical' | 'horizontal' = 'horizontal';
  currentStep: Steps = Steps.ExhibitorInfo;
  steps = Steps;
  states: State[] = [];
  counties: County[] = [];
  registration: Registration = { state: 'TX', liveStocks: [] };
  registrationResponse: RegistrationResponse | undefined;
  orgId: number = 0;
  orgConfigs!: OrgConfigs;
  isRegistrationNotStarted: boolean = false;
  isRegistrationCompleted: boolean = false;
  isRegistrationStarted: boolean = false;
  paymentStatus: string | undefined;
  exhibitorId: number | undefined;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly registrationService: RegistrationService,
    private readonly notificationService: NotificationService,
    private readonly dataService: DataService
  ) { }

  private updateOrientation(): void {
    const width = window.innerWidth;
    this.stepperOrientation = width < 768 ? 'vertical' : 'horizontal';
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.updateOrientation();
  }

  ngOnInit(): void {
    this.updateOrientation();

    this.orgConfigs = this.dataService.orgConfigs;
    this.orgId = this.orgConfigs.orgId;

    const currentDate = new Date();
    const startDate = new Date(this.orgConfigs.events.registrationStart);
    const endDate = new Date(this.orgConfigs.events.registrationEnd);

    if (currentDate <= startDate) {
      this.isRegistrationNotStarted = true;
    } else if (currentDate >= endDate) {
      this.isRegistrationCompleted = true;
    } else {
      this.isRegistrationStarted = true;

      this.registrationService.getCounties().subscribe({
        next: response => {
          this.counties = response.sort((a, b) => a.county.localeCompare(b.county));
        },
        error: err => {
          this.notificationService.error(err);
        }
      });
    }

    this.route.data.subscribe(data => {
      this.paymentStatus = data['paymentStatus'];

      if (this.paymentStatus) {
        this.exhibitorId = Number(this.route.snapshot.paramMap.get('exhibitorId')) || undefined;
        this.registration.id = this.exhibitorId;

        if (this.paymentStatus == 'cancelled' || this.paymentStatus == 'status') {
          this.currentStep = Steps.Confirmation;
          if (this.paymentStatus == 'status') {
            this.loadPaymentStatus();
          }
        }
      } else {
        this.states = this.registrationService.getStates();
        this.registration.orgId = this.orgConfigs.orgId;
      }
    });
  }

  private loadPaymentStatus(): void {
    this.paymentStatus = 'loading';

    const maxRetries = 4; // 4 retries + 1 initial = 5 total calls
    const retryDelay = 5000;
    let attempt = 1;

    const fetchExhibitor$ = () =>
      defer(() => {
        console.log(`Get payment status. Attempt ${attempt}`);
        return this.registrationService.getExhibitorById(this.orgId, this.exhibitorId ?? 0);
      }).pipe(
        catchError((err) => {
          console.log(`Get payment status attempt ${attempt} failed.`);
          return of(null); // Continue retries even if error
        })
      );

    // Recursive polling until success or attempts exhausted
    fetchExhibitor$()
      .pipe(
        expand((response) => {
          // Stop polling if success
          if (response?.transactionStatus?.paymentStatus) {
            this.paymentStatus = 'success';
            return EMPTY; // stops expand()
          }

          // Retry if not success and limit not reached
          if (attempt <= maxRetries) {
            console.log(`Payment not successful yet. Retrying in ${retryDelay}ms...`);
            attempt++;
            return timer(retryDelay).pipe(
              tap(() => console.log(`Retry attempt ${attempt}`)),
              switchMap(() => fetchExhibitor$())
            );
          }

          // Stop after max retries
          this.paymentStatus = 'failed';
          return EMPTY;
        }),
        finalize(() => {
          console.log(`Polling completed with paymentStatus: ${this.paymentStatus}`);
        })
      )
      .subscribe((response) => {
        if (response && response.transactionStatus) {
          this.registration = response;
          this.registration.totalPayable = this.registration.liveStocks.reduce((sum, item) => sum + item.totalPrice, 0);
        }
      });
  }

  onPreviousClick(): void {
    this.currentStep--;
  }

  onNextClick(btn: Button) {
    if (this.currentStep == Steps.ExhibitorInfo) {
      this.handleExhibitorInfoStep();
    } else if (this.currentStep == Steps.Livestocks) {
      this.handleLivestockStep(btn);
    } else if (this.currentStep == Steps.Disclosures) {
      this.handleFormStatus(btn);
    }
  }

  private handleExhibitorInfoStep(): void {
    if (this.registration.firstName
      && this.registration.lastName
      && this.registration.ageDiscloserRequired
      && !this.registration.ageDiscloserAccepted) {
      this.exhibitorInfo.showAgeDisclosures();
    } else if (!this.exhibitorInfo.validateForm()) {
      this.notificationService.error({ message: 'Please provide all required details to proceed.' });
    } else {
      this.registration = this.exhibitorInfo.getRegistrationDetails();
      if (!this.registration.qualityCounts) {
        this.notificationService.error({ message: 'You cannot proceed without Quality Counts.', sticky: true });
      } else {
        this.currentStep++;
      }
    }
  }

  private handleLivestockStep(btn: Button): void {
    const livestocks: Livestock[] = this.livestocksInfo.getLivestocks();
    this.registration.liveStocks = livestocks;

    let totalPayable = 0;
    livestocks.flatMap(ls => ls.totalPrice).forEach(total => { totalPayable += total; });
    this.registration.totalPayable = totalPayable;

    if (livestocks.length == 0) {
      this.notificationService.error({ message: 'Please add atleast one entry to proceed.' });
    } else {
      btn.loading = true;

      const $obserable = this.registration.id ? this.registrationService.updateExhibitor(this.registration) : this.registrationService.addExhibitor(this.registration);

      $obserable.subscribe({
        next: (response) => {
          this.registration.id = response.id;
          this.currentStep++;
          btn.loading = false;
        },
        error: (err) => {
          btn.loading = false;
          this.notificationService.error(err);
        }
      });
    }
  }

  private handleFormStatus(btn: Button): void {
    btn.loading = true;

    if (!this.additionalInfo.aggrementConfirmation) {
      this.notificationService.error({ message: 'Please accept disclosure to proceed.' });
      btn.loading = false;
    } else {
      const maxRetries = 2; // 2 retries + 1 initial = 5 total calls
      const retryDelay = 3000;

      this.registrationService.getExhibitorById(this.orgId, this.registration.id ?? 0).pipe(
        // Keep polling until status = 'completed' or 3 total attempts
        expand((response, i) => {
          // Stop if completed
          if (CommonUtils.equalsIgnoreCase('completed', response.formStatus) || i >= maxRetries) {
            return of();
          }

          return timer(retryDelay).pipe(
            switchMap(() => this.registrationService.getExhibitorById(this.orgId, this.registration.id ?? 0))
          );
        }),
        // Continue until status = completed
        takeWhile((response, i) => !CommonUtils.equalsIgnoreCase('completed', response.formStatus) && i < 2, true),
        last(), // emit only the final result
        catchError(err => {
          console.error('Error checking jotform status.', err);
          return of(null);
        })
      ).subscribe(response => {
        if (response) {
          this.registration.formStatus = response.formStatus;
          // this.registration.waiverComplete = CommonUtils.equalsIgnoreCase('completed', this.registration.formStatus);
          this.registration.waiverComplete = true;

          if (!this.registration.waiverComplete) {
            this.notificationService.error({ message: 'Please read and electronically sign the Waiver Form to proceed.' });
          } else {
            this.currentStep++;
          }
        }

        btn.loading = false;
      });
    }
  }

  onPaymentSuccess(registrationResponse: RegistrationResponse) {
    if (registrationResponse?.paymentResponse.status) {
      this.registrationResponse = registrationResponse;
      this.currentStep++;
    }
  }

  getProgress(): number {
    return this.currentStep * 100 / (Object.keys(Steps).length / 2);
  }

}
